import * as THREE from 'three';

import colorConstant from '../../../color';
import {cssToThreeColor, cssToHexColor} from '../color_transform';

import {addUserData} from './three_utils';

const PROJECT_OPACITY = 0.5;
const OBSTRUCTION_OPACITY = 0.35;

export const INVISIBLE_MATERIAL = new THREE.MeshBasicMaterial({
  colorWrite: false,
  depthWrite: false,
});
INVISIBLE_MATERIAL.userData.shared = true;

export const SOIL_MATERIAL = new THREE.MeshLambertMaterial({
  color: 0x0a3c0a,
  transparent: true,
  opacity: 0.6,
});

export const VIS_A_VIS_MATERIAL = addUserData(
  new THREE.MeshLambertMaterial({
    color: 0x3778b3,
    transparent: true,
    opacity: 0.6,
  }),
  {shared: true},
);

type UserData = {
  userData: {
    shared: true;
    materialName: string;
  };
};

function createBuildingMaterial(
  name: string,
  color: string,
  transparent: boolean,
  opacity: number,
): THREE.MeshLambertMaterial & UserData {
  return addUserData(
    new THREE.MeshLambertMaterial({
      color: cssToThreeColor(color),
      transparent,
      opacity,
    }),
    {
      materialName: name,
      shared: true,
    },
  );
}

export const EDGES_MATERIAL: THREE.LineBasicMaterial & UserData = addUserData(
  new THREE.LineBasicMaterial({
    color: cssToHexColor(colorConstant.grayL),
  }),
  {
    materialName: 'EDGES',
    shared: true,
  },
);

export const COMBLES_EDGES_MATERIAL: THREE.LineBasicMaterial & UserData =
  addUserData(
    new THREE.LineBasicMaterial({
      color: cssToHexColor(colorConstant.grayXL),
    }),
    {
      materialName: 'COMBLES_EDGES',
      shared: true,
    },
  );

export const BUILDING_MATERIALS: Record<
  string,
  (
    | THREE.MeshLambertMaterial
    | THREE.MeshBasicMaterial
    | THREE.LineBasicMaterial
  ) &
    UserData
> = {
  PROJECT_OPAQUE: createBuildingMaterial(
    'PROJECT_OPAQUE',
    colorConstant.projectBuilding,
    false,
    1,
  ),
  PROJECT_OPAQUE_SELECTED: createBuildingMaterial(
    'PROJECT_OPAQUE_SELECTED',
    colorConstant.middleYellow,
    false,
    1,
  ),
  PROJECT_OPAQUE_ERROR: createBuildingMaterial(
    'PROJECT_OPAQUE_ERROR',
    colorConstant.complementary,
    false,
    1,
  ),
  PROJECT_OPAQUE_SELECTED_ERROR: createBuildingMaterial(
    'PROJECT_OPAQUE_SELECTED_ERROR',
    colorConstant.yellowOrange,
    false,
    1,
  ),
  PROJECT_TRANSPARENT: createBuildingMaterial(
    'PROJECT_TRANSPARENT',
    colorConstant.projectBuilding,
    true,
    PROJECT_OPACITY,
  ),
  PROJECT_TRANSPARENT_SELECTED: createBuildingMaterial(
    'PROJECT_TRANSPARENT_SELECTED',
    colorConstant.middleYellow,
    true,
    PROJECT_OPACITY,
  ),
  PROJECT_TRANSPARENT_ERROR: createBuildingMaterial(
    'PROJECT_TRANSPARENT_ERROR',
    colorConstant.complementary,
    true,
    PROJECT_OPACITY,
  ),
  PROJECT_TRANSPARENT_SELECTED_ERROR: createBuildingMaterial(
    'PROJECT_TRANSPARENT_SELECTED_ERROR',
    colorConstant.yellowOrange,
    true,
    PROJECT_OPACITY,
  ),
  NON_PROJECT: createBuildingMaterial(
    'NON_PROJECT',
    colorConstant.nonProjectBuilding,
    false,
    1,
  ),
  NON_PROJECT_TRANSPARENT: createBuildingMaterial(
    'NON_PROJECT_TRANSPARENT',
    colorConstant.nonProjectBuilding,
    true,
    OBSTRUCTION_OPACITY,
  ),
  NON_PROJECT_SELECTED: createBuildingMaterial(
    'NON_PROJECT_SELECTED',
    colorConstant.middleYellow,
    false,
    1,
  ),
  REMARKABLE: createBuildingMaterial(
    'REMARKABLE',
    colorConstant.alabaster,
    false,
    1,
  ),
  REMARKABLE_TRANSPARENT: createBuildingMaterial(
    'REMARKABLE_TRANSPARENT',
    colorConstant.alabaster,
    true,
    OBSTRUCTION_OPACITY,
  ),
  ARCHITECTURAL_SEQUENCE: createBuildingMaterial(
    'ARCHITECTURAL_SEQUENCE',
    colorConstant.architecturalSequence,
    false,
    1,
  ),
  ARCHITECTURAL_SEQUENCE_TRANSPARENT: createBuildingMaterial(
    'ARCHITECTURAL_SEQUENCE_TRANSPARENT',
    colorConstant.architecturalSequence,
    true,
    OBSTRUCTION_OPACITY,
  ),
  NON_EDITABLE: createBuildingMaterial(
    'NON_EDITABLE',
    colorConstant.nonEditableBuilding,
    false,
    1,
  ), //will be filled by a procedurally generated texture
  WINDOW_TEXT: addUserData(
    new THREE.MeshBasicMaterial({
      color: 0x000000,
    }),
    {
      materialName: 'WINDOW_TEXT',
      shared: true,
    },
  ),
  EDGES: EDGES_MATERIAL,
  COMBLES_EDGES: COMBLES_EDGES_MATERIAL,
  PROJECT_FACECAP: addUserData(
    new THREE.MeshLambertMaterial({
      side: THREE.DoubleSide,
      color: cssToHexColor(colorConstant.grayXL),
      stencilWrite: true,
      stencilRef: 0,
      stencilFunc: THREE.NotEqualStencilFunc,
      stencilFail: THREE.ReplaceStencilOp,
      stencilZFail: THREE.ReplaceStencilOp,
      stencilZPass: THREE.ReplaceStencilOp,
    }),
    {
      materialName: 'PROJECT_FACECAP',
      shared: true,
    },
  ),
  NON_PROJECT_FACECAP: addUserData(
    new THREE.MeshLambertMaterial({
      side: THREE.DoubleSide,
      color: cssToHexColor(colorConstant.grayM),
      stencilWrite: true,
      stencilRef: 0,
      stencilFunc: THREE.NotEqualStencilFunc,
      stencilFail: THREE.ReplaceStencilOp,
      stencilZFail: THREE.ReplaceStencilOp,
      stencilZPass: THREE.ReplaceStencilOp,
    }),
    {
      materialName: 'NON_PROJECT_FACECAP',
      shared: true,
    },
  ),
  AMPUTATION: createBuildingMaterial(
    'AMPUTATION',
    colorConstant.grayL,
    false,
    1,
  ),
  CONSTRUCTION_PILLARS: createBuildingMaterial(
    'CONSTRUCTION_PILLARS',
    colorConstant.grayXXL,
    false,
    1,
  ),
  CONSTRUCTION_WALLS: createBuildingMaterial(
    'CONSTRUCTION_WALLS',
    colorConstant.projectBuilding,
    true,
    PROJECT_OPACITY,
  ),
};

export const PARKING_MATERIALS = {
  FLOOR: addUserData(
    new THREE.MeshLambertMaterial({
      color: cssToThreeColor(colorConstant.asphaltColor),
      transparent: false,
      opacity: 1,
    }),
    {
      materialName: 'FLOOR',
      shared: true,
    },
  ),
  MARKINGS: addUserData(
    new THREE.MeshLambertMaterial({
      color: cssToThreeColor(colorConstant.alabaster),
      transparent: false,
      opacity: 1,
    }),
    {
      materialName: 'MARKINGS',
      shared: true,
    },
  ),
  BIKE_PARKING: addUserData(
    new THREE.MeshLambertMaterial({
      color: cssToThreeColor(colorConstant.vividSkyBlue),
      transparent: false,
      opacity: 1,
    }),
    {
      materialName: 'BIKE_PARKING',
      shared: true,
    },
  ),
  CORE: addUserData(
    new THREE.MeshLambertMaterial({
      color: cssToThreeColor(colorConstant.alabaster),
      transparent: false,
      opacity: 1,
    }),
    {
      materialName: 'CORE',
      shared: true,
    },
  ),
};

enum ConstructionColor {
  InsulatedConcrete = '#d9bf93',
  NonInsulatedConcrete = '#A8A8A8',
  Brick = '#B25F45',
  Glass = '#e4f0f5',
}

export const CONSTRUCTION_MATERIALS = {
  INSULATED_CONCRETE: createBuildingMaterial(
    'INSULATED_CONCRETE',
    ConstructionColor.InsulatedConcrete,
    false,
    1,
  ),
  INSULATED_CONCRETE_TRANSPARENT: createBuildingMaterial(
    'INSULATED_CONCRETE_TRANSPARENT',
    ConstructionColor.InsulatedConcrete,
    true,
    PROJECT_OPACITY,
  ),
  NON_INSULATED_CONCRETE: createBuildingMaterial(
    'NON_INSULATED_CONCRETE',
    ConstructionColor.NonInsulatedConcrete,
    false,
    1,
  ),
  NON_INSULATED_CONCRETE_TRANSPARENT: createBuildingMaterial(
    'NON_INSULATED_CONCRETE_TRANSPARENT',
    ConstructionColor.NonInsulatedConcrete,
    true,
    PROJECT_OPACITY,
  ),
  BRICK: createBuildingMaterial('BRICK', ConstructionColor.Brick, false, 1),
  BRICK_TRANSPARENT: createBuildingMaterial(
    'BRICK_TRANSPARENT',
    ConstructionColor.Brick,
    true,
    PROJECT_OPACITY,
  ),
  GLASS: createBuildingMaterial('GLASS', ConstructionColor.Glass, true, 0.3),
};

export const CLIPPING_MATERIALS: Record<string, THREE.Material> = {
  BACK_STENCIL_CLIPPING: new THREE.MeshBasicMaterial({
    depthWrite: false,
    depthTest: false,
    colorWrite: false,
    stencilWrite: true,
    stencilFunc: THREE.AlwaysStencilFunc,
    side: THREE.BackSide,
    stencilFail: THREE.IncrementWrapStencilOp,
    stencilZFail: THREE.IncrementWrapStencilOp,
    stencilZPass: THREE.IncrementWrapStencilOp,
  }),
  FRONT_STENCIL_CLIPPING: new THREE.MeshBasicMaterial({
    depthWrite: false,
    depthTest: false,
    colorWrite: false,
    stencilWrite: true,
    stencilFunc: THREE.AlwaysStencilFunc,
    side: THREE.FrontSide,
    stencilFail: THREE.DecrementWrapStencilOp,
    stencilZFail: THREE.DecrementWrapStencilOp,
    stencilZPass: THREE.DecrementWrapStencilOp,
  }),
};

CLIPPING_MATERIALS.BACK_STENCIL_CLIPPING.userData.shared = true;
CLIPPING_MATERIALS.FRONT_STENCIL_CLIPPING.userData.shared = true;

const materialsToConvert = [
  BUILDING_MATERIALS.PROJECT_OPAQUE,
  BUILDING_MATERIALS.PROJECT_OPAQUE_SELECTED,
  BUILDING_MATERIALS.PROJECT_OPAQUE_ERROR,
  BUILDING_MATERIALS.PROJECT_OPAQUE_SELECTED_ERROR,
  BUILDING_MATERIALS.PROJECT_TRANSPARENT,
  BUILDING_MATERIALS.PROJECT_TRANSPARENT_SELECTED,
  BUILDING_MATERIALS.PROJECT_TRANSPARENT_ERROR,
  BUILDING_MATERIALS.PROJECT_TRANSPARENT_SELECTED_ERROR,
  BUILDING_MATERIALS.NON_PROJECT,
  BUILDING_MATERIALS.NON_PROJECT_SELECTED,
  BUILDING_MATERIALS.REMARKABLE,
  BUILDING_MATERIALS.ARCHITECTURAL_SEQUENCE,
  BUILDING_MATERIALS.WINDOW_TEXT,
  BUILDING_MATERIALS.EDGES,
  BUILDING_MATERIALS.COMBLES_EDGES,
  BUILDING_MATERIALS.NON_EDITABLE,
];

for (const material of materialsToConvert) {
  const materialName = material.userData.materialName;
  CLIPPING_MATERIALS[materialName] = material.clone();
  CLIPPING_MATERIALS[materialName].clipShadows = true;
}

export const SELECTION_MATERIALS = [
  BUILDING_MATERIALS.PROJECT_OPAQUE_SELECTED,
  BUILDING_MATERIALS.PROJECT_OPAQUE_SELECTED_ERROR,
  BUILDING_MATERIALS.PROJECT_TRANSPARENT_SELECTED,
  BUILDING_MATERIALS.PROJECT_TRANSPARENT_SELECTED_ERROR,
  BUILDING_MATERIALS.NON_PROJECT_SELECTED,
];

export type InteriorDesignMaterial = THREE.Material & {
  userData: {isInteriorDesign: true};
};
export function isInteriorDesignMaterial(
  mat: THREE.Material,
): mat is InteriorDesignMaterial {
  return mat.userData.isInteriorDesign === true;
}
